import {
  Badge,
  DescriptionList,
  EmptyState,
  ErrorState,
  Grid,
  Icon,
  IconButton,
  Inline,
  Metric,
  PageHeader,
  SegmentedControl,
  Skeleton,
  Table,
  Toolbar,
  badgeToneForStatus,
} from "@evolua/ui";
import { EvoluaApiError } from "@evolua/api-client";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useManagerTeamIndicators } from "../api/use-manager.js";

function shiftPeriod(
  isoDay: string,
  period: "month" | "quarter" | "year",
  delta: number,
): string {
  const d = new Date(`${isoDay}T00:00:00.000Z`);
  if (period === "year") {
    d.setUTCFullYear(d.getUTCFullYear() + delta, 0, 1);
  } else if (period === "quarter") {
    d.setUTCMonth(d.getUTCMonth() + delta * 3);
    const q = Math.floor(d.getUTCMonth() / 3) * 3;
    d.setUTCMonth(q, 1);
  } else {
    d.setUTCMonth(d.getUTCMonth() + delta, 1);
  }
  return d.toISOString().slice(0, 10);
}

const PERIOD_ITEMS = [
  { id: "month", label: "Mês" },
  { id: "quarter", label: "Trimestre" },
  { id: "year", label: "Ano" },
] as const;

/** Indicadores da equipe do gestor (FASE-06-T46). */
export function IndicadoresEquipePage() {
  const [period, setPeriod] = useState<"month" | "quarter" | "year">("month");
  const [anchor, setAnchor] = useState("2026-08-01");
  const query = useMemo(() => ({ from: anchor, period }), [anchor, period]);
  const indicators = useManagerTeamIndicators(query);

  const correlationId =
    indicators.error instanceof EvoluaApiError
      ? indicators.error.problem.traceId
      : undefined;

  return (
    <>
      <PageHeader
        title="Indicadores da equipe"
        lead="Headcount, admissões, desligamentos, absenteísmo e férias vencendo — apenas a subárvore do gestor, com períodos comparáveis."
      />

      <Toolbar>
        <Inline gap={2}>
          <IconButton
            label="Período anterior"
            onClick={() => setAnchor((a) => shiftPeriod(a, period, -1))}
          >
            <Icon name="chevron-left" size={18} />
          </IconButton>
          <IconButton
            label="Próximo período"
            onClick={() => setAnchor((a) => shiftPeriod(a, period, 1))}
          >
            <Icon name="chevron-right" size={18} />
          </IconButton>
        </Inline>
        <SegmentedControl
          ariaLabel="Granularidade do período"
          items={PERIOD_ITEMS}
          value={period}
          onChange={(id) => setPeriod(id as "month" | "quarter" | "year")}
        />
        {indicators.data ? (
          <Badge tone="neutral">
            {indicators.data.from} → {indicators.data.to}
          </Badge>
        ) : null}
      </Toolbar>

      {indicators.isPending ? (
        <Skeleton height="8rem" label="Indicadores" />
      ) : indicators.isError ? (
        <ErrorState
          {...(correlationId ? { correlationId } : {})}
          onRetry={() => void indicators.refetch()}
        />
      ) : !indicators.data ? (
        <EmptyState
          title="Sem dados"
          description="Não há indicadores para este período."
        />
      ) : (
        <div className="evolua-stack evolua-stack--3">
          <Grid min="12rem">
            <Metric
              label="Headcount"
              value={indicators.data.teamSize}
              hint={`ant. ${indicators.data.previous.teamSize}`}
            />
            <Metric
              label="Admissões"
              value={indicators.data.hires}
              hint={`ant. ${indicators.data.previous.hires}`}
            />
            <Metric
              label="Desligamentos"
              value={indicators.data.terminations}
              hint={`ant. ${indicators.data.previous.terminations}`}
            />
            <Metric
              label="Absenteísmo"
              value={`${indicators.data.absenteeismPercent}%`}
              hint={`ant. ${indicators.data.previous.absenteeismPercent}%`}
            />
          </Grid>

          <section>
            <h2 className="evolua-section-title">Ausências no período</h2>
            {indicators.data.absencesInPeriod.length === 0 ? (
              <p className="evolua-page-lead">Nenhuma ausência no intervalo.</p>
            ) : (
              <Table
                caption="Ausências no período"
                getRowId={(a) => `${a.employeeId}-${a.startDate}`}
                columns={[
                  {
                    id: "name",
                    header: "Colaborador",
                    cell: (a) => <Link to={`/equipe/${a.employeeId}`}>{a.fullName}</Link>,
                  },
                  {
                    id: "status",
                    header: "Status",
                    cell: (a) => (
                      <Badge tone={badgeToneForStatus(a.status)}>
                        {a.status === "ON_LEAVE" ? "Afastado" : "Férias"}
                      </Badge>
                    ),
                  },
                  {
                    id: "days",
                    header: "Dias",
                    align: "numeric",
                    cell: (a) => a.daysInPeriod,
                  },
                ]}
                rows={indicators.data.absencesInPeriod}
              />
            )}
          </section>

          <section>
            <h2 className="evolua-section-title">Férias vencendo</h2>
            {indicators.data.vacationsExpiring.length === 0 ? (
              <p className="evolua-page-lead">Nenhum saldo a vencer no período.</p>
            ) : (
              <Table
                caption="Férias vencendo"
                getRowId={(v) => v.employeeId}
                columns={[
                  {
                    id: "name",
                    header: "Colaborador",
                    cell: (v) => <Link to={`/equipe/${v.employeeId}`}>{v.fullName}</Link>,
                  },
                  {
                    id: "balance",
                    header: "Saldo",
                    align: "numeric",
                    cell: (v) => `${v.balanceDays} dias`,
                  },
                  { id: "limit", header: "Limite", cell: (v) => v.limitDate },
                ]}
                rows={indicators.data.vacationsExpiring}
              />
            )}
          </section>

          <section>
            <h2 className="evolua-section-title">Fórmulas</h2>
            <DescriptionList
              items={[
                { term: "Headcount", description: indicators.data.formulas.headcount },
                { term: "Admissões", description: indicators.data.formulas.hires },
                {
                  term: "Desligamentos",
                  description: indicators.data.formulas.terminations,
                },
                {
                  term: "Absenteísmo",
                  description: indicators.data.formulas.absenteeism,
                },
                {
                  term: "Férias vencendo",
                  description: indicators.data.formulas.vacationsExpiring,
                },
              ]}
            />
          </section>
        </div>
      )}

      <p className="evolua-mt">
        <Link to="/calendario-ferias">Calendário de férias →</Link>
        {" · "}
        <Link to="/">Dashboard →</Link>
      </p>
    </>
  );
}
